import { useQuery } from '@tanstack/react-query'
import { kpisApi } from '../api/kpis'
import type { Kpi } from '../lib/types'

interface KpiSelectorProps {
  selected: string[]
  custom: string
  onChange: (selected: string[], custom: string) => void
  // Metas por KPI (metric -> valor como string do input). Só aparecem nos marcados.
  targets: Record<string, string>
  onTargetChange: (metric: string, value: string) => void
  disabled?: boolean
}

const GROUP_ORDER: Array<{ category: string; title: string }> = [
  { category: 'performance', title: 'Performance' },
  { category: 'cost', title: 'Custo' },
  { category: 'engagement', title: 'Engajamento' },
  { category: 'conversion', title: 'Conversão' },
]

export function KpiSelector({
  selected,
  custom,
  onChange,
  targets,
  onTargetChange,
  disabled,
}: KpiSelectorProps) {
  const { data, isLoading } = useQuery({ queryKey: ['kpis'], queryFn: () => kpisApi.list() })
  const kpis = data?.kpis ?? []

  function toggle(key: string) {
    const next = selected.includes(key)
      ? selected.filter((k) => k !== key)
      : [...selected, key]
    onChange(next, custom)
  }

  const grouped = GROUP_ORDER.map((g) => ({
    ...g,
    items: kpis.filter((k) => k.category === g.category),
  })).filter((g) => g.items.length > 0)

  // KPIs de categorias não previstas em GROUP_ORDER
  const known = new Set(GROUP_ORDER.map((g) => g.category))
  const others = kpis.filter((k) => !known.has(k.category))

  return (
    <div className="space-y-4">
      {isLoading && <p className="text-sm text-slate-500">Carregando KPIs…</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        {grouped.map((group) => (
          <fieldset key={group.category} className="rounded-lg border border-slate-200 p-3">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {group.title}
            </legend>
            <div className="space-y-1.5">
              {group.items.map((kpi) => (
                <KpiCheckbox
                  key={kpi.id}
                  kpi={kpi}
                  checked={selected.includes(kpi.key)}
                  onToggle={() => toggle(kpi.key)}
                  target={targets[kpi.key] ?? ''}
                  onTargetChange={(v) => onTargetChange(kpi.key, v)}
                  disabled={disabled}
                />
              ))}
            </div>
          </fieldset>
        ))}

        {others.length > 0 && (
          <fieldset className="rounded-lg border border-slate-200 p-3">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Outros
            </legend>
            <div className="space-y-1.5">
              {others.map((kpi) => (
                <KpiCheckbox
                  key={kpi.id}
                  kpi={kpi}
                  checked={selected.includes(kpi.key)}
                  onToggle={() => toggle(kpi.key)}
                  target={targets[kpi.key] ?? ''}
                  onTargetChange={(v) => onTargetChange(kpi.key, v)}
                  disabled={disabled}
                />
              ))}
            </div>
          </fieldset>
        )}
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">
          KPIs customizados
        </label>
        <input
          value={custom}
          disabled={disabled}
          onChange={(e) => onChange(selected, e.target.value)}
          placeholder="campo_windsor:Label Exibido, outro_campo:Outro Label"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none disabled:bg-slate-50"
        />
        <p className="mt-1 text-xs text-slate-400">
          Ex.: <code>leads:Leads Gerados, purchase_value:Valor de Compra</code>
        </p>
      </div>
    </div>
  )
}

function KpiCheckbox({
  kpi,
  checked,
  onToggle,
  target,
  onTargetChange,
  disabled,
}: {
  kpi: Kpi
  checked: boolean
  onToggle: () => void
  target: string
  onTargetChange: (value: string) => void
  disabled?: boolean
}) {
  // 'lower' = menor é melhor → meta é teto (máx.); senão meta é piso (mín.)
  const kind = kpi.betterWhen === 'lower' ? 'máx.' : 'mín.'
  return (
    <div className="flex items-center justify-between gap-2">
      <label className="flex cursor-pointer items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
          className="mt-0.5 rounded text-brand-600 focus:ring-brand-500"
        />
        <span>
          <span className="font-medium text-slate-700">{kpi.label}</span>
          <span className="ml-1 text-xs text-slate-400">({kpi.windsorField})</span>
        </span>
      </label>
      {checked && (
        <div className="flex shrink-0 items-center gap-1" title={`Meta ${kind} (opcional)`}>
          <span className="text-[10px] font-medium uppercase text-slate-400">meta {kind}</span>
          <input
            type="number"
            step="any"
            min="0"
            inputMode="decimal"
            value={target}
            disabled={disabled}
            onChange={(e) => onTargetChange(e.target.value)}
            placeholder="—"
            className="w-16 rounded border border-slate-300 px-1.5 py-0.5 text-right text-xs focus:border-brand-500 focus:outline-none disabled:bg-slate-50"
          />
        </div>
      )}
    </div>
  )
}
